import { defineStore } from "pinia";
import { ref, computed, watch } from "vue";
import api from "@/services/api";
import {
    useNotification,
    useValidationErrors,
    useLocalization,
    useConfig,
    useHelpers,
    usePlanCodes,
    useMpesaPaymentRequest
} from "@/composables";

export const useSignupWizardStore = defineStore('signup-wizard', () => {
    const { showSuccess, showError } = useNotification();
    const {
        validationErrors,
        setValidationErrors,
        resetValidationErrors,
        hasValidationErrors,
        getValidationErrors
    } = useValidationErrors();

    const {isKeLocale} = useLocalization();

    const {
        publicMarketOnboardingKey,
        loginUrl,
        standardSubscriptionMonthlyPrice,
        standardSubscriptionAnnualPrice,
        enterpriseSubscriptionMonthlyPrice,

    } = useConfig();

    const {phoneWithoutZero} = useHelpers();

    const {
        standardSubscriptionMonthlyPaystackPlanCode,
        standardSubscriptionAnnualPaystackPlanCode,
        enterpriseSubscriptionMonthlyPaystackPlanCode
    } = usePlanCodes();

    const { processing, makeMpesaPaymentRequest } = useMpesaPaymentRequest();

    const DEV_REF_CODE = "5190";

    const currentStep = ref(0);
    const setCurrentStep = (value) => {
        currentStep.value = value
    }
    const resetCurrentStep = () => {
        setCurrentStep(0)
    }

    const isLoading = ref(false);
    const setIsLoading = (value) => {
        isLoading.value = value
    }

    const countries = ref([
        {
            code: "NG",
            name: "Nigeria",
            dialCode: "+234",
            flag: "https://flagcdn.com/w40/ng.png",
        },
        {
            code: "KE",
            name: "Kenya",
            dialCode: "+254",
            flag: "https://flagcdn.com/w40/ke.png",
        },
    ]);

    const defaultCountry = computed(() => {
        return countries.value.find((c) => isKeLocale.value ? c.code === "KE" : c.code === "NG");
    });
    const selectedCountry = ref(defaultCountry.value);
    const setSelectedCountry = (value) => {
        selectedCountry.value = value
    }
    const selectCountry = (value) => {
        setSelectedCountry(value)
    }

    const formData = ref({
        id: null,
        referral_code: "",
        phone: "",
        payload: {
            market_name: "",
            organization_name: "",
            market_id: "",
            email: "",
            country: selectedCountry.value.code,
            country_id: "",
            first_name: "",
            last_name: "",
            address: "",
            state_id: "",
            phone:"",
            lga_id: "",
            city: "",
            market_admin_firstname: "",
            market_admin_lastname: "",
            otp: "",
            subscription_plan: null,
            transaction: null,
            hubs: [],
            access_type: "paid",
        },
        accept_terms: false,
        password: "",
        password_confirmation: "",
        phoneWithDialCode: "",
    });

    const setFormData = (value) => {
        let payload = {}

        if(value.payload){
            payload = {
                ...formData.value.payload,
                ...value.payload
            }

            delete value.payload
        }else{
            payload = formData.value.payload
        }

        formData.value = {
            ...formData.value,
            ...value,
        };

        formData.value.payload = {
            ...payload
        };
    }

    const signupPath = ref(null);
    const setSignupPath = (value) => {
        signupPath.value = value
    }

    const hubTemplate = {
        name: "",
        leader_name: "",
        leader_phone: "",
        state_id: "",
        lga_id: "",
        address: "",
        country_name: selectedCountry.value.name,
        country_code: selectedCountry.value.dialCode,
        country_flag: selectedCountry.value.flag,
    }

    const addHub = () => {
        formData.value.payload.hubs.push({...hubTemplate})
    }

    const removeHub = (index) => {
        formData.value.payload.hubs.splice(index, 1)
    }

    const fetchSignupState = async () => {
        try {
            const signupId = localStorage.getItem("signupId");
            if (!signupId) return;

            const res = await api.get(`/signup/activities/${signupId}`);

            if (!res.data || Object.keys(res.data).length === 0) {
                console.error("No signup data found for the given ID.");
                localStorage.clear();
                resetCurrentStep();
                return;
            }

            const { id, current_step, referral_code, phone, payload } = res.data.data;

            if(current_step >= 10){
                localStorage.clear();
                resetCurrentStep();
                return;
            }

            setFormData({
                id,
                referral_code,
                current_step,
                phone,
                phoneWithDialCode: payload?.phone ?? "",
                payload: {
                    ...payload,
                    phone,
                }
            });

            updateCurrentStep(current_step);
        } catch (error) {
            console.log("Failed to load registration state", error.response)
            console.log("Failed to load registration state", error)
            console.error("Failed to load registration state", error);
            localStorage.clear();
            resetCurrentStep();
        }
    };

    const validateReferralCode = async () => {
        setIsLoading(true);
        resetValidationErrors();

        try {
            const code = formData.value.referral_code?.trim();
            if (!code) {
                setValidationErrors({
                    referral_code: "Referral code is required."
                })
                setIsLoading(false);
                return;
            }
            if (import.meta.env.VITE_APP_ENV !== "local" && code === DEV_REF_CODE) {
                setValidationErrors({
                    referral_code: "Kindly provide a valid referral code."
                })
                setIsLoading(false);
                return;
            }

            const res = await api.post(
                `/signup/start/${code}`,
            );

            showSuccess('Referal code verification successful')
            formData.value.id = res.data.data.id;
            localStorage.setItem("signupId", res?.data?.data?.id);

            updateCurrentStep(res?.data?.data?.current_step);
        } catch (err) {
            const errorMessage = err?.response?.data?.message || "Error verifying referal code"
            console.error(err);
            showError(errorMessage)
        } finally {
            setIsLoading(false);
        }
    };

    const  updateCurrentStep = (step) => {
        setCurrentStep(step);
        localStorage.setItem("currentStep", currentStep.value);
    };

    const handlePrevious = async (newStep = null) => {
        setIsLoading(true);
        resetValidationErrors();

        let step = currentStep.value - 1;

        if(newStep){
            step = newStep;
        }

        if(step === 2 || step === 0){
            step = 1;
        }


        try {
            const response = await api.put(
                `/signup/activities/${formData.value.id}/update`,
                {
                    current_step: step,
                }
            );
            updateCurrentStep(response?.data?.data?.current_step);
        } catch (error) {
            //   errors.value = error.response.data.errors;
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    const goBack = async () =>  {
        if(currentStep.value == 0){
            window.history.back()
        }else{
            await handlePrevious()
        }

    };

    function selectSignupPath(path) {
        if(signupPath.value === path){
            setSignupPath(null)
        }else{
            setSignupPath(path)
        }
    }

    const validateForm = () => {
        resetValidationErrors();

        if (signupPath.value === null) {
            setValidationErrors({
                market_option: "Please select an option."
            })
        }
    };

    const syncSignupPath = async () => {
        setIsLoading(true);
        resetValidationErrors();

        if(!signupPath.value){
            setValidationErrors({
                market_option: "Please select an option."
            })
            setIsLoading(false);
            return;
        }

        let payload = {};

        if(signupPath.value === "join"){
            setValidationErrors({
                market_option: "This path is not yet available."
            })
            setIsLoading(false);
            return;
        }

        if (signupPath.value === "create") {
            payload.current_step = 3;
        }

        // Get plan from local storage
        const plan = JSON.parse(localStorage.getItem("plan"));
        if(plan){
            payload = {
                ...payload,
                payload: {
                    subscription_plan: {...plan},
                },
            };
        }

        try {
            const response = await api.put(
                `/signup/activities/${formData.value.id}/update`,
                payload,
            );
            updateCurrentStep(response?.data?.data?.current_step);
            showSuccess("Successful")
        } catch (error) {
            console.error(error);
            showError("Something went wrong")
        } finally {
            setIsLoading(false);
        }
    };

    const submitUserInfo = async () => {
        setIsLoading(true);
        resetValidationErrors();

        if(!publicMarketOnboardingKey){
            console.error("Public market onboarding key is not available")
            showError("Something went wrong")
            setIsLoading(false);
            return;
        }

        const data = {
            payload: {
                first_name: formData.value.payload.first_name,
                last_name: formData.value.payload.last_name,
                email: formData.value.payload.email || undefined,
                phone: formData.value.payload.phone,
            },
            current_step: currentStep.value + 1,
            phone: formData.value.payload.phone,
            accept_terms: formData.value.accept_terms,
        }

        try {
            const response = await api.put(
                `/signup/activities/${formData.value.id}/update`,
                {
                    ...data,
                }
            );

            api.post("/otp/send", {
                key: publicMarketOnboardingKey + ':' + phoneWithoutZero(formData.value.payload.phone),
                phone: formData.value.payload.phone,
                ttl: 300,
            });

            updateCurrentStep(response?.data?.data?.current_step);
            showSuccess("User information saved successfully")
        } catch (error) {
            let errorMessage = 'Something went wrong';
            if(error.response.data.errors){
                setValidationErrors(error.response.data.errors)
                if(error.response.data.message){
                    errorMessage = error.response.data.message
                }
            }
            showError(errorMessage)
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    const validateOtp = async () => {
        setIsLoading(true);
        resetValidationErrors();
        if (formData.value.payload.otp.length < 4) {
            setValidationErrors({
                otp: "Please enter the full 4-digit code."
            })
            setIsLoading(false);
            return;
        }

        const trimmedPhone = phoneWithoutZero(formData.value.phone)
        try {
            const response = await api.post("/otp/validate", {
                key:
                    publicMarketOnboardingKey +
                    ":" +
                    trimmedPhone,
                phone: formData.value.phone,
                otp: formData.value.payload.otp,
            });

            if (response.data.success) {
                let payload = {
                    current_step: currentStep.value + 1,
                }

                // If user has a subscription plan, skip the subscription plan step
                if(hasSubscriptionPlan.value){
                    payload.current_step = currentStep.value + 2;
                }

                const res = await api.put(
                    `/signup/activities/${formData.value.id}/update`,
                    payload,
                );
                // Reset OTP
                formData.value.payload.otp = '';
                updateCurrentStep(res?.data?.data?.current_step);
                showSuccess("Successful")
            } else {
                showError(response.data.message)
            }
        } catch (error) {
            console.error(error);
            let errorMessage = 'Failed to verify OTP.'

            if(error.response.data.errors){
                setValidationErrors(error.response.data.errors)
            }

            if(error.response.data.message){
                errorMessage = error.response.data.message
            }
            showError(errorMessage)
        } finally {
            setIsLoading(false);
        }
    };

    const submitSubscriptionPlan = async () => {
        setIsLoading(true);

        if(!formData.value.payload?.subscription_plan){
            showError("Please select a plan.")
            setIsLoading(false);
            return;
        }

        const data = {
            payload: {
                // ...formData.value.payload,
                subscription_plan: {
                    ...formData.value.payload.subscription_plan,
                }
            },
            current_step: currentStep.value + 1,
        }

        try {
            const response = await api.put(
                `/signup/activities/${formData.value.id}/update`,
                {
                    ...data,
                }
            );

            updateCurrentStep(response?.data?.data?.current_step);
            showSuccess("Subscription plan saved successfully")
        } catch (error) {
            let errorMessage = 'Something went wrong';
            if(error.response.data.errors){
                setValidationErrors(error.response.data.errors)
                if(error.response.data.message){
                    errorMessage = error.response.data.message
                }
            }
            showError(errorMessage)
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    const deleteRedundantFields = (data) => {
        delete data.payload.subscription_plan;
        if(!data.payload.transaction){
            delete data.payload.transaction;
        }
    }

    const submitOrganizationSetup = async () => {
        setIsLoading(true);
        resetValidationErrors();

        const hubs = formData.value.payload.hubs.map((hub) => {
            return {
                ...hub,
                leader_name: hub.leader_name ? hub.leader_name : undefined,
                leader_phone: hub.leader_name ? hub.leader_phone ?? "" : undefined,
                state_id: !hub.state_id ? "" : hub.state_id,
                lga_id: !hub.lga_id ? "" : hub.lga_id,
            };
        })

        const data = {
            payload: {
                ...formData.value.payload,
                email: isStandardPlan.value ? (formData.value.payload.email || undefined) : formData.value.payload.email,
                hubs: [...hubs],
            },
            current_step: currentStep.value + 1,
        }

        deleteRedundantFields(data);

        try {
            const response = await api.put(
                `/signup/activities/${formData.value.id}/update`,
                {
                    ...data,
                }
            );

            updateCurrentStep(response?.data?.data?.current_step);
            showSuccess("Organization setup saved successfully")
        } catch (error) {
            let errorMessage = 'Something went wrong';
            if(error.response.data.errors){
                setValidationErrors(error.response.data.errors)
                if(error.response.data.message){
                    errorMessage = error.response.data.message
                }
            }
            showError(errorMessage)
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    }

    const setCountryId = (id) => {
        setFormData({
            payload: {
                country_id: id,
            }
        })
    }

    const selectAccessType = (accessType) => {
        setFormData({
            payload: {
                ...formData.value.payload,
                access_type: accessType
            }
        });
    }

    const setTransaction = (transaction) => {
        setFormData({
            payload: {
                ...formData.value.payload,
                transaction: {
                    ...formData.value.payload.transaction,
                    ...transaction
                }
            }
        });
    }

    const handleSignupActivityUpdated = (e) => {
        console.log('SignupActivityUpdated', e);
        updateCurrentStep(e?.data?.current_step);
        showSuccess("Organization setup completed successfully")
    }

    const completeSetup = async () => {
        setIsLoading(true);

        if(!formData.value.payload.access_type){
            showError('Please select a plan')
            setIsLoading(false)
            return
        }

        let missingPaymentObject = isKeLocale.value ? (!formData.value?.payload?.transaction?.MerchantRequestID)
            : (!formData.value?.payload?.transaction?.transaction)

        if(formData.value?.payload?.access_type === 'paid' && missingPaymentObject){
            showError('Please select a payment method')
            setIsLoading(false)
            return
        }

        const data = {
            payload: {
                ...formData.value.payload,
                // If the subscription plan is standard and access type is free or locale is Kenya, then the email is optional
                email: ((isStandardPlan.value && formData.value?.payload?.access_type === 'free') || isKeLocale.value)
                    ? (formData.value.payload.email || undefined)
                    : formData.value.payload.email,
                transaction: {
                    ...formData.value.payload.transaction,
                }
            },
            current_step: currentStep.value + 1,
        }

        deleteRedundantFields(data);

        try {
            // To-do: get sockets working
            // Echo.channel(`signup-activity-log.${formData.value.id}`)
            // .listen('.signupActivityUpdated', handleSignupActivityUpdated);
            const response = await api.put(
                `/signup/activities/${formData.value.id}/update`,
                {
                    ...data,
                }
            );

            updateCurrentStep(response?.data?.data?.current_step);
            showSuccess("Organization setup completed successfully")
        } catch (error) {
            let errorMessage = 'Something went wrong';
            if(error.response?.data?.errors){
                setValidationErrors(error.response.data.errors)
                if(error.response?.data?.message){
                    errorMessage = error.response.data.message
                }
            }
            showError(errorMessage)
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    }

    const redirectToCreatePassword = async () => {
        setIsLoading(true);
        const data = {
            current_step: currentStep.value + 1,
        }

        try {
            const response = await api.put(
                `/signup/activities/${formData.value.id}/update`,
                {
                    ...data,
                }
            );

            updateCurrentStep(response?.data?.data?.current_step);
        } catch (error) {
            let errorMessage = 'Something went wrong';
            if(error.response.data.errors){
                setValidationErrors(error.response.data.errors)
                if(error.response.data.message){
                    errorMessage = error.response.data.message
                }
            }
            showError(errorMessage)
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    }

    const validatePassword = () => {
        const validationErrors = {};

        if(!formData.value.accept_terms){
            validationErrors.accept_terms = ["Please accept terms and conditions."]
        }

        if(!formData.value.password){
            validationErrors.password = ["Please enter password."]
        }

        if(!formData.value.password_confirmation){
            validationErrors.password_confirmation = ["Please enter password confirmation."]
        }

        if(formData.value.password !== formData.value.password_confirmation){
            validationErrors.password_confirmation = ["Passwords do not match."]
        }

        if(formData.value.password && formData.value.password.length < 6){
            validationErrors.password = ["Password must be at least 6 characters long."]
        }

        if(Object.keys(validationErrors).length > 0){
            setValidationErrors(validationErrors)
        }

        return !hasValidationErrors.value;
    }

    const submitPassword = async () => {
        resetValidationErrors();
        setIsLoading(true);

        if(!validatePassword()){
            setIsLoading(false);
            return;
        }

        const data = {
            payload: {
                password: formData.value.password,
                password_confirmation: formData.value.password_confirmation,
            },
            current_step: currentStep.value + 1,
        }

        try {
            const response = await api.put(
                `/signup/activities/${formData.value.id}/update`,
                {
                    ...data,
                }
            );

            showSuccess("Password set successfully");

            if(response.data?.success){
                // clear local storage
                localStorage.clear();
                // redirect to login
                window.location.href = loginUrl;
            }else{
                console.log(response.data?.message)
                showError('Something went wrong')
            }
        } catch (error) {
            let errorMessage = 'Something went wrong';
            if(error.response.data.errors){
                setValidationErrors(error.response.data.errors)
                if(error.response.data.message){
                    errorMessage = error.response.data.message
                }
            }
            showError(errorMessage)
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    }

    const toggleAcceptTerms = () => {
        setFormData({
            accept_terms: !formData.value.accept_terms
        })
    }

    const processKePayment = async () => {
        setIsLoading(true);
        const data = {
            amount: subscriptionPlanPrice.value,
            phone: formData.value.payload.phone,
        }
        const response = await makeMpesaPaymentRequest(data);
        console.log('response data', response.data)
        if(response.data?.success){
            console.log('mpesa response', response);
            setTransaction({
                ...response.data.data,
            })
            showSuccess(response.data?.message || "Payment request sent successfully");
        }else{
            showError(response.data?.message || "Failed to send payment request");
        }
        setIsLoading(false);
        completeSetup()
    }

    const hasSubscriptionPlan = computed(() => {
        return formData.value.payload?.subscription_plan;
    })

    const isStandardPlan = computed(() => {
        return formData.value.payload?.subscription_plan?.name === "standard";
    })

    const subscriptionPlanPrice = computed(() => {
        if(isStandardPlan.value){
            return formData.value.payload?.subscription_plan?.yearly
                ? standardSubscriptionAnnualPrice
                : standardSubscriptionMonthlyPrice;
        }

        return enterpriseSubscriptionMonthlyPrice;
    })

    const subscriptionPlanPaystackPlanCode = computed(() => {
        if(isStandardPlan.value){
            return formData.value.payload?.subscription_plan?.yearly
                ? standardSubscriptionAnnualPaystackPlanCode
                : standardSubscriptionMonthlyPaystackPlanCode;
        }

        return enterpriseSubscriptionMonthlyPaystackPlanCode;
    })

    const subscriptionPlanInterval = computed(() => {
        return formData.value.payload?.subscription_plan?.yearly ? 'annually' : 'monthly';
    })

    watch(() => currentStep.value, async (value) => {
        if(value){
            await fetchSignupState();
        }
    }, { immediate: true })

    return {
        isLoading,
        currentStep,
        updateCurrentStep,
        handlePrevious,
        goBack,
        fetchSignupState,
        formData,
        setFormData,
        validateReferralCode,
        validationErrors,
        signupPath,
        setSignupPath,
        selectSignupPath,
        hasValidationErrors,
        validateForm,
        syncSignupPath,
        selectedCountry,
        submitUserInfo,
        countries,
        selectCountry,
        getValidationErrors,
        validateOtp,
        submitSubscriptionPlan,
        addHub,
        removeHub,
        setCountryId,
        submitOrganizationSetup,
        selectAccessType,
        completeSetup,
        redirectToCreatePassword,
        toggleAcceptTerms,
        submitPassword,
        isStandardPlan,
        setTransaction,
        subscriptionPlanPrice,
        subscriptionPlanInterval,
        processKePayment,
        isKeLocale,
        subscriptionPlanPaystackPlanCode,
        makeMpesaPaymentRequest,
    }
})
